import {
  BENEFICIARY_REQUIREMENT_REGEX_MAX_LENGTH,
  BENEFICIARY_REQUIREMENT_VALUE_MAX_LENGTH,
} from '../internal/send-money.constants';

const SAFE_ESCAPED_CHARACTERS = new Set('dDsSwW.^$*+?()[]{}|\\-/');
const REGEX_META_CHARACTERS = new Set('\\^$.*+?()[]{}|');

const isSafeLiteral = (character: string): boolean => {
  const code = character.charCodeAt(0);
  return code >= 32 && code <= 126 && !REGEX_META_CHARACTERS.has(character);
};

const consumeCharacterClass = (source: string, start: number): number | null => {
  let index = start + 1;
  let hasContent = false;

  if (source[index] === '^') {
    index += 1;
  }

  while (index < source.length) {
    const character = source[index];
    if (character === ']') {
      return hasContent ? index + 1 : null;
    }

    if (character === '\\') {
      const escapedCharacter = source[index + 1];
      if (escapedCharacter === undefined || !SAFE_ESCAPED_CHARACTERS.has(escapedCharacter)) {
        return null;
      }
      index += 2;
      hasContent = true;
      continue;
    }

    const code = character.charCodeAt(0);
    if (character === '[' || code < 32 || code > 126) {
      return null;
    }

    index += 1;
    hasContent = true;
  }

  return null;
};

const consumeAtom = (source: string, start: number): number | null => {
  const character = source[start];
  if (character === '\\') {
    const escapedCharacter = source[start + 1];
    return escapedCharacter !== undefined && SAFE_ESCAPED_CHARACTERS.has(escapedCharacter)
      ? start + 2
      : null;
  }

  if (character === '[') {
    return consumeCharacterClass(source, start);
  }

  return character !== undefined && isSafeLiteral(character) ? start + 1 : null;
};

const isAllowedBound = (value: string, allowZero: boolean): boolean => {
  if (!/^(0|[1-9][0-9]{0,2})$/.test(value)) {
    return false;
  }

  const bound = Number(value);
  return bound <= BENEFICIARY_REQUIREMENT_VALUE_MAX_LENGTH && (allowZero || bound > 0);
};

export const isSafeBeneficiaryRequirementRegex = (source: string): boolean => {
  if (
    source.length === 0 ||
    source.length > BENEFICIARY_REQUIREMENT_REGEX_MAX_LENGTH ||
    source[0] !== '^' ||
    source.at(-1) !== '$'
  ) {
    return false;
  }

  const body = source.slice(1, -1);
  let index = 0;
  let atomCount = 0;

  while (index < body.length) {
    const atomEnd = consumeAtom(body, index);
    if (atomEnd === null) {
      if (body[index] !== '(' || atomCount === 0 || body.at(-2) !== ')' || body.at(-1) !== '?') {
        return false;
      }

      const optionalGroupBody = body.slice(index + 1, -2);
      if (optionalGroupBody.length === 0) {
        return false;
      }

      let groupIndex = 0;
      let groupAtomCount = 0;
      while (groupIndex < optionalGroupBody.length) {
        const groupAtomEnd = consumeAtom(optionalGroupBody, groupIndex);
        if (groupAtomEnd === null) {
          return false;
        }

        groupAtomCount += 1;
        groupIndex = groupAtomEnd;
        if (optionalGroupBody[groupIndex] !== '{') {
          continue;
        }

        const quantifierEnd = optionalGroupBody.indexOf('}', groupIndex + 1);
        if (quantifierEnd === -1) {
          return false;
        }

        const quantifier = optionalGroupBody.slice(groupIndex + 1, quantifierEnd);
        if (!isAllowedBound(quantifier, true)) {
          return false;
        }

        groupIndex = quantifierEnd + 1;
      }

      return groupAtomCount > 0;
    }

    atomCount += 1;
    index = atomEnd;
    if (body[index] !== '{') {
      continue;
    }

    const quantifierEnd = body.indexOf('}', index + 1);
    if (quantifierEnd === -1) {
      return false;
    }

    const quantifier = body.slice(index + 1, quantifierEnd);
    const terminalRange = quantifier.match(/^0,([1-9][0-9]{0,2})$/);
    if (terminalRange !== null) {
      return quantifierEnd === body.length - 1 && isAllowedBound(terminalRange[1] ?? '', false);
    }

    if (!isAllowedBound(quantifier, true)) {
      return false;
    }

    index = quantifierEnd + 1;
  }

  return atomCount > 0;
};

export const matchesBeneficiaryRequirementRegex = (
  value: string,
  source: string | undefined,
): boolean => {
  if (source === undefined) {
    return true;
  }

  if (
    value.length > BENEFICIARY_REQUIREMENT_VALUE_MAX_LENGTH ||
    !isSafeBeneficiaryRequirementRegex(source)
  ) {
    return false;
  }

  try {
    return new RegExp(source).test(value);
  } catch {
    return false;
  }
};
